// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, List, ListItem, Tooltip, Typography } from "@mui/material";
import { useTheme } from "@mui/system";
import { useState } from "react";
import { IAvailableProject } from "Utils/typings/state/folder";
import CloseIcon from '@mui/icons-material/Close';
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined';
import { getAllFoldersThunk, moveBulkProjectsToFolderThunk } from "Features/folderList/folderListThunk";
import { useAppDispatch } from "Hooks";
import { useParams } from "react-router-dom";
import { SetFoldersAction } from "Features/projectList/projectListActions";
import stylesDef from "./styles";

interface MoveProjectBulkDialogProps {
  open: boolean;
  onClose: () => void;
  availableProjects: IAvailableProject[];
  loadingAvailableProjects: boolean;
}

const MoveProjectBulkDialog = ({ open, onClose, availableProjects, loadingAvailableProjects }: MoveProjectBulkDialogProps) => {
  const theme = useTheme();
  const styles = stylesDef(theme);
  const dispatch = useAppDispatch();
  const { folderId } = useParams();
  const [selectedProjects, setSelectedProjects] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const handleMoveProjects = async () => {
    setLoading(true);
    const response = await dispatch(moveBulkProjectsToFolderThunk(selectedProjects, folderId as string));
    if (response.success) {
      await dispatch(getAllFoldersThunk());
      onClose();
      setLoading(false);
    } else {
      setLoading(false);
    }
  };

  const handleSelectProject = (projectId: string) => {
    if (selectedProjects.includes(projectId)) {
      setSelectedProjects(selectedProjects.filter((id) => id !== projectId));
    } else {
      setSelectedProjects([...selectedProjects, projectId]);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} sx={styles.dialogContainer}>
      <DialogTitle sx={styles.dialogTitle}>
        <Typography sx={styles.dialogTitleText}>Add Projects</Typography>
        <IconButton 
          onClick={onClose}
          sx={styles.dialogTitleCloseButton}
        >
          <CloseIcon sx={styles.dialogTitleCloseButtonIcon} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={styles.dialogContent}>
        <Typography variant="body1">Please select below the project(s) that you want to add to this folder:</Typography>
        <Box sx={styles.dialogContentProjectsContainer}>
          <List sx={styles.dialogContentProjectsList}>
            {loadingAvailableProjects ? (
              <Box sx={styles.dialogProgressContainer}>
                <CircularProgress size={40} sx={styles.dialogContentProjectsListProgress}/>
              </Box>
            ) : (
              availableProjects.map((project) => (
              <ListItem 
                key={project._id} 
                sx={styles.dialogContentProjectsListItem}
              >
                <NoteAddOutlinedIcon
                  sx={selectedProjects.includes(project._id) ? styles.dialogContentProjectsListItemIconSelected : styles.dialogContentProjectsListItemIcon}
                />
                <Tooltip title={project.name} arrow placement="top">
                  <Typography sx={styles.dialogContentProjectsListItemText}>{project.name}</Typography>
                </Tooltip>
                <Button
                  variant="text" 
                  color="primary" 
                  onClick={() => handleSelectProject(project._id)} 
                  sx={selectedProjects.includes(project._id) ? styles.dialogContentProjectsListItemButtonSelected : styles.dialogContentProjectsListItemButton}
                >
                  {selectedProjects.includes(project._id) ? 'Remove' : 'Add'}
                </Button>
              </ListItem>
            )))}
          </List>
        </Box>
      </DialogContent>
      <DialogActions sx={styles.dialogActions}>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={handleMoveProjects} 
          disabled={loading || selectedProjects.length === 0}
          sx={styles.dialogActionsButton}
        >
          {
            loading ? (
              <CircularProgress size={20} sx={styles.dialogActionsButtonProgress} />
            ) : (
              'Add Projects'
            )
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MoveProjectBulkDialog;